import {
  CheckCircle2,
  Download,
  FileJson2,
  LogOut,
  Save,
  Settings2,
  Upload
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { BankConfig, LeadRecord } from "../../shared/contracts";
import { Brand } from "../components/Brand";
import {
  adminLogin,
  adminLogout,
  getAdminBanks,
  getAdminLeads,
  getAdminSession,
  saveAdminBanks
} from "../lib/api";
import { downloadText } from "../lib/format";

const numericFields: Array<{
  key: keyof BankConfig;
  label: string;
  step?: number;
}> = [
  { key: "effectiveAnnualRateSac", label: "Taxa SAC % a.a.", step: 0.01 },
  { key: "effectiveAnnualRatePrice", label: "Taxa Price % a.a.", step: 0.01 },
  { key: "maxTermMonths", label: "Prazo máx. (meses)" },
  { key: "maxLtvPct", label: "Quota máx. (%)", step: 0.1 },
  { key: "maxAgeAtEnd", label: "Idade máx. final" },
  { key: "incomeCommitmentPct", label: "Renda comprometida (%)", step: 0.1 },
  { key: "monthlyAdminFee", label: "Tarifa mensal (R$)", step: 0.01 },
  { key: "upfrontFee", label: "Tarifa inicial (R$)", step: 0.01 },
  { key: "monthlyInsuranceRatePct", label: "Seguro mensal (%)", step: 0.001 }
];

function leadsToCsv(leads: LeadRecord[]): string {
  const rows = [
    ["Data", "Nome", "E-mail", "Telefone", "Imóvel", "Entrada", "Renda", "Prazo", "Banco", "Sistema"],
    ...leads.map((lead) => [
      lead.createdAt,
      lead.name,
      lead.email,
      lead.phone,
      lead.simulation.input.propertyValue,
      lead.simulation.input.downPayment,
      lead.simulation.input.monthlyIncome,
      lead.simulation.input.termMonths,
      lead.simulation.selectedOption?.bankId ?? "",
      lead.simulation.selectedOption?.system ?? ""
    ])
  ];
  return rows
    .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(";"))
    .join("\n");
}

export function AdminPage() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [banks, setBanks] = useState<BankConfig[]>([]);
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState<"banks" | "leads">("banks");
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getAdminSession()
      .then(setAuthenticated)
      .catch(() => setAuthenticated(false));
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    Promise.all([getAdminBanks(), getAdminLeads()])
      .then(([loadedBanks, loadedLeads]) => {
        setBanks(loadedBanks);
        setLeads(loadedLeads);
      })
      .catch((error: unknown) => {
        setStatus("error");
        setMessage(error instanceof Error ? error.message : "Falha ao carregar o painel.");
      });
  }, [authenticated]);

  async function login(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = String(new FormData(event.currentTarget).get("token") ?? "");
    setMessage("");
    try {
      await adminLogin(token);
      setAuthenticated(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Acesso não autorizado.");
    }
  }

  async function save() {
    setStatus("saving");
    setMessage("");
    try {
      setBanks(await saveAdminBanks(banks));
      setStatus("saved");
      setMessage("Condições publicadas.");
      window.setTimeout(() => setStatus("idle"), 2_000);
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar.");
    }
  }

  function updateBank(index: number, patch: Partial<BankConfig>) {
    setBanks((current) =>
      current.map((bank, bankIndex) => (bankIndex === index ? { ...bank, ...patch } : bank))
    );
    setStatus("idle");
  }

  async function importJson(file?: File) {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text()) as BankConfig[];
      if (!Array.isArray(parsed) || parsed.length === 0) throw new Error("Arquivo sem instituições.");
      setBanks(parsed);
      setStatus("idle");
      setMessage("JSON importado. Revise e clique em Publicar alterações.");
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "JSON inválido.");
    } finally {
      if (importRef.current) importRef.current.value = "";
    }
  }

  if (authenticated === null) {
    return (
      <main className="admin-auth">
        <div className="state-card" role="status">
          <Settings2 className="spin-slow" aria-hidden="true" />
          <h1>Verificando sessão</h1>
        </div>
      </main>
    );
  }

  if (!authenticated) {
    return (
      <main className="admin-auth">
        <div className="admin-auth__brand">
          <Brand />
        </div>
        <section className="admin-login" aria-labelledby="admin-title">
          <span className="section-kicker">Painel protegido</span>
          <h1 id="admin-title">Premissas sob controle.</h1>
          <p>
            Acesse com a credencial administrativa configurada no ambiente. Nenhuma chave fica
            armazenada no navegador.
          </p>
          <form onSubmit={login}>
            <label>
              <span>Credencial de acesso</span>
              <input
                name="token"
                type="password"
                autoComplete="current-password"
                minLength={24}
                required
                autoFocus
              />
            </label>
            {message && (
              <p className="form-error" role="alert">
                {message}
              </p>
            )}
            <button className="button button--primary button--wide" type="submit">
              Entrar no painel
            </button>
          </form>
          <a href="/">← Voltar ao simulador</a>
        </section>
      </main>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <Brand />
        <div>
          <span>Configuração operacional</span>
          <button
            className="button button--soft"
            type="button"
            onClick={async () => {
              await adminLogout();
              setAuthenticated(false);
            }}
          >
            <LogOut size={16} aria-hidden="true" /> Sair
          </button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-title">
          <div>
            <span className="section-kicker">Painel de premissas</span>
            <h1>Instituições e oportunidades</h1>
            <p>Edite parâmetros sem alterar código. Mudanças entram no simulador ao publicar.</p>
          </div>
          <div className="admin-tabs" role="tablist" aria-label="Seções do painel">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "banks"}
              onClick={() => setActiveTab("banks")}
            >
              Instituições <span>{banks.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "leads"}
              onClick={() => setActiveTab("leads")}
            >
              Leads <span>{leads.length}</span>
            </button>
          </div>
        </div>

        {activeTab === "banks" ? (
          <>
            <div className="admin-toolbar">
              <div>
                <button
                  className="button button--soft"
                  type="button"
                  onClick={() =>
                    downloadText(
                      "alicerce-bancos.json",
                      JSON.stringify(banks, null, 2),
                      "application/json"
                    )
                  }
                >
                  <FileJson2 size={17} aria-hidden="true" /> Exportar JSON
                </button>
                <button
                  className="button button--soft"
                  type="button"
                  onClick={() => importRef.current?.click()}
                >
                  <Upload size={17} aria-hidden="true" /> Importar JSON
                </button>
                <input
                  ref={importRef}
                  className="sr-only"
                  type="file"
                  accept="application/json,.json"
                  onChange={(event) => importJson(event.target.files?.[0])}
                />
              </div>
              <button
                className="button button--primary"
                type="button"
                onClick={save}
                disabled={status === "saving"}
              >
                {status === "saved" ? (
                  <CheckCircle2 size={17} aria-hidden="true" />
                ) : (
                  <Save size={17} aria-hidden="true" />
                )}
                {status === "saving" ? "Publicando…" : "Publicar alterações"}
              </button>
            </div>

            {message && (
              <p className={status === "error" ? "admin-message is-error" : "admin-message"}>
                {message}
              </p>
            )}

            <div className="bank-editor-list">
              {banks.map((bank, index) => (
                <article className="bank-editor" key={bank.id}>
                  <div className="bank-editor__heading">
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={bank.enabled}
                        onChange={(event) => updateBank(index, { enabled: event.target.checked })}
                      />
                      <span aria-hidden="true" />
                      {bank.enabled ? "Ativa" : "Oculta"}
                    </label>
                    <span
                      className="bank-editor__accent"
                      style={{ backgroundColor: bank.accent }}
                      aria-hidden="true"
                    />
                    <div>
                      <strong>{bank.name}</strong>
                      <small>{bank.id}</small>
                    </div>
                  </div>
                  <div className="bank-editor__identity">
                    <label>
                      <span>Nome completo</span>
                      <input
                        value={bank.name}
                        maxLength={80}
                        onChange={(event) => updateBank(index, { name: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Nome curto</span>
                      <input
                        value={bank.shortName}
                        maxLength={30}
                        onChange={(event) => updateBank(index, { shortName: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Cor</span>
                      <span className="color-input">
                        <input
                          type="color"
                          value={bank.accent}
                          onChange={(event) => updateBank(index, { accent: event.target.value })}
                        />
                        <code>{bank.accent}</code>
                      </span>
                    </label>
                  </div>
                  <div className="bank-editor__fields">
                    {numericFields.map((field) => (
                      <label key={field.key}>
                        <span>{field.label}</span>
                        <input
                          type="number"
                          step={field.step ?? 1}
                          min={0}
                          value={Number(bank[field.key])}
                          onChange={(event) =>
                            updateBank(index, {
                              [field.key]: Number(event.target.value)
                            } as Partial<BankConfig>)
                          }
                        />
                      </label>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <section className="leads-panel">
            <div className="admin-toolbar">
              <p>
                Últimos <strong>{leads.length}</strong> pedidos de contato, em ordem de chegada.
              </p>
              <button
                className="button button--soft"
                type="button"
                disabled={leads.length === 0}
                onClick={() =>
                  downloadText(
                    "alicerce-leads.csv",
                    `\uFEFF${leadsToCsv(leads)}`,
                    "text/csv;charset=utf-8"
                  )
                }
              >
                <Download size={17} aria-hidden="true" /> Exportar CSV
              </button>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Recebido</th>
                    <th>Contato</th>
                    <th>Cenário</th>
                    <th>Opção</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id}>
                      <td>{new Date(lead.createdAt).toLocaleString("pt-BR")}</td>
                      <td>
                        <strong>{lead.name}</strong>
                        <small>{lead.email}</small>
                        <small>{lead.phone}</small>
                      </td>
                      <td>
                        R$ {lead.simulation.input.propertyValue.toLocaleString("pt-BR")} ·{" "}
                        {Math.round(lead.simulation.input.termMonths / 12)} anos
                      </td>
                      <td>
                        {lead.simulation.selectedOption
                          ? `${lead.simulation.selectedOption.bankId} · ${lead.simulation.selectedOption.system.toUpperCase()}`
                          : "Sem seleção"}
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr>
                      <td colSpan={4} className="empty-cell">
                        Nenhum pedido de contato recebido.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
